import { useQuery } from '@apollo/client';
import type { Filter } from '@coursera-web/cds-labs';
import { MODULE_LAYOUTS } from '@coursera-web/cds-labs';

import type { Skill } from '@coursera/grpc-types-skills/coursera/proto/skills/v2/skill';
import type {
  MultiGetSkillsRequest,
  MultiGetSkillsResponse,
  QuerySkillsRequest,
  QuerySkillsResponse,
} from '@coursera/grpc-types-skills/coursera/proto/skills/v2/skills_api';

import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import browseInSerpQueryMapper from 'bundles/search-common/components/experiments/browseInSerp/BrowseInSerpQueryMapper';
import {
  MultiGetSkillsQuery,
  QuerySkillsParentIdQuery,
  QuerySkillsQuery,
} from 'bundles/search-common/components/experiments/browseInSerp/browseInSerpQueries';
import { getHitSlug } from 'bundles/search-common/components/search-cards/SearchCards';
import {
  BROWSE_IN_SEARCH_BEGINNER_INDEX_ID,
  BROWSE_IN_SEARCH_MOST_POPULAR_INDEX_ID,
  LEARNING_PRODUCTS_ENUM_MAP,
  NUMBER_OF_RESULTS_PER_COLLECTION,
  PRODUCT_DIFFICULTY_LEVELS,
  PRODUCT_DURATIONS_STRING,
  getTranslatedDurations,
} from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchProductDifficultyLevel, SearchProductHit } from 'bundles/search-common/providers/searchTypes';
import { getIsPathwayContent } from 'bundles/search-common/utils/SearchUtils';

import _t from 'i18n!nls/search-common';

type QuerySkillsVariables = {
  input: Partial<QuerySkillsRequest>;
};
type QuerySkillsGQLResponse = { QuerySkills: QuerySkillsResponse };

type MultiGetSkillsVariables = {
  input: MultiGetSkillsRequest;
};
type MultiGetSkillsGQLResponse = { MultiGetSkills: MultiGetSkillsResponse };

// SORTING LOGIC - Use childSkillIds list and occupationIds list to determine the sorted order of response sibling/child skills
const CHILD_SKILL_WEIGHT = 0.6;
const OCCUPATION_WEIGHT = 0.4;
const MAX_DESCRIPTION_CHARACTERS = 350;

const calculateWeightedScore = (skill: Skill): number =>
  skill.childSkillIds.length * CHILD_SKILL_WEIGHT + skill.occupationIds.length * OCCUPATION_WEIGHT;

export const sortSkillsByWeightedScore = (skillA: Skill, skillB: Skill): number => {
  const weightedScoreA = calculateWeightedScore(skillA);
  const weightedScoreB = calculateWeightedScore(skillB);
  return weightedScoreB - weightedScoreA;
};
// END OF SORTING LOGIC

const replaceAmpersandWithAnd = (str: string): string => {
  return typeof str === 'string' ? str.replace(/\s*&\s*/g, ' and ').trim() : str;
};

export const useQueryMatchSkillTaxonomy2 = (query: string | null): Skill | null => {
  const lowerCaseQuery = replaceAmpersandWithAnd(query?.toLowerCase() || '');
  const mappedQuery = browseInSerpQueryMapper[lowerCaseQuery]?.toLowerCase() || lowerCaseQuery;
  const { data } = useQuery<QuerySkillsGQLResponse, QuerySkillsVariables>(QuerySkillsQuery, {
    variables: {
      input: {
        skillNames: [mappedQuery],
        includeOnlyEligibleForScoring: false, // show all skills
        skillSorts: ['SKILL_SORT_GRANULARITY_ASC'], // sort by higher tier first
        enableEpic: true, // force skill taxonomy 2.0
      },
    },
    skip: !query, // skip if query is null (not in test variant)
  });

  // find exact name match
  const matchedSkill = data?.QuerySkills?.skills?.find((skill: Skill) => {
    const skillName = skill.skillName.toLowerCase();
    const directMatch = skillName === lowerCaseQuery || skillName === mappedQuery;
    if (directMatch) return true;
    return false;
  });
  if (matchedSkill) return matchedSkill;

  // otherwise if there is only one skill in the response, it's a match as well
  if (!matchedSkill && data?.QuerySkills?.skills?.length === 1) {
    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return data?.QuerySkills.skills[0];
  }

  return null;
};

export const useBrowseInSerpCollection = () => {
  const { getSearchResults, isLoading } = useSearch();
  const mostPopular = getSearchResults(BROWSE_IN_SEARCH_MOST_POPULAR_INDEX_ID);
  const beginnerFriendly = getSearchResults(BROWSE_IN_SEARCH_BEGINNER_INDEX_ID);
  return (
    mostPopular?.elements?.length === NUMBER_OF_RESULTS_PER_COLLECTION ||
    (beginnerFriendly?.elements?.length || 0) >= NUMBER_OF_RESULTS_PER_COLLECTION ||
    isLoading
  );
};

export const useBrowseInSerpRelatedSkills = (currentSkill: Skill) => {
  let shouldSkipQuerySkillsQuery: boolean = true;
  let shouldSkipMultiGetQuery: boolean = true;

  if (currentSkill.parentSkillIds.length) {
    shouldSkipQuerySkillsQuery = false;
  } else if (currentSkill.childSkillIds.length) {
    shouldSkipMultiGetQuery = false;
  }

  // fetch sibling skills
  const { data: querySkillsResponse, loading: querySkillsIsLoading } = useQuery<
    QuerySkillsGQLResponse,
    QuerySkillsVariables
  >(QuerySkillsParentIdQuery, {
    variables: {
      input: {
        includeOnlyEligibleForScoring: false,
        skillSorts: ['SKILL_SORT_GRANULARITY_ASC'], // sort by higher tier first
        enableEpic: true,
        parentSkillIds: currentSkill.parentSkillIds,
      },
    },
    skip: shouldSkipQuerySkillsQuery, // skip if skill has no sibling skills
  });

  // fetch child skills
  const { data: multiGetResponse, loading: multiGetSkillsIsLoading } = useQuery<
    MultiGetSkillsGQLResponse,
    MultiGetSkillsVariables
  >(MultiGetSkillsQuery, {
    variables: {
      input: {
        skillIds: currentSkill.childSkillIds,
        enableEpic: true,
      },
    },
    skip: shouldSkipMultiGetQuery, // skip if skill has no child skills
  });

  const siblingSkillList = querySkillsResponse?.QuerySkills.skills.filter((skill: Skill) => {
    return skill.skillId !== currentSkill.skillId;
  });
  const childSkillList = multiGetResponse?.MultiGetSkills.skills;

  const siblingSkills = siblingSkillList?.sort(sortSkillsByWeightedScore) ?? null;

  const childSkills = childSkillList?.toSorted(sortSkillsByWeightedScore) ?? null;

  if (!shouldSkipQuerySkillsQuery) {
    return {
      data: siblingSkills,
      loading: querySkillsIsLoading,
    };
  } else if (!shouldSkipMultiGetQuery) {
    return {
      data: childSkills,
      loading: multiGetSkillsIsLoading,
    };
  }

  return null;
};

export const truncateSkillDescription = (description: string) => {
  const sentences = description.split(/(?<!\b\w\.)\s*\.\s*(?=[A-Z])|\n/);
  const singleSentenceDescription = sentences[0] + (sentences.length > 1 ? '.' : '');
  const twoSentenceDescription = sentences.slice(0, 2).join('. ') + (sentences.length > 2 ? '.' : '');

  if (twoSentenceDescription.length <= MAX_DESCRIPTION_CHARACTERS) {
    return twoSentenceDescription;
  } else return singleSentenceDescription;
};

type CollectionSets = {
  collectionTitle: string;
  collectionId: string;
  entries: SearchProductHit[];
}[];

export const getBrowseInSearchCollectionsData = (
  skillTaxonomyData: Skill | null,
  mostPopularElements: SearchProductHit[] | undefined,
  beginnerFriendlyElements: SearchProductHit[] | undefined,
  isLoading?: boolean
) => {
  if (!skillTaxonomyData?.skillName) return null;
  const collectionSets: CollectionSets = [];
  const filters: Filter[] = [];
  if (mostPopularElements && mostPopularElements.length === NUMBER_OF_RESULTS_PER_COLLECTION) {
    collectionSets.push({
      collectionId: 'most-popular',
      collectionTitle: _t('Most Popular'),
      entries: mostPopularElements,
    });
    filters.push({ id: 'most-popular', filterName: _t('Most Popular') });
  }
  const beginnerFriendlyFilteredElements = beginnerFriendlyElements
    ?.filter((element) => !mostPopularElements?.some((popularElement) => popularElement.id === element.id))
    .slice(0, 3); // remove duplicates
  if (
    beginnerFriendlyFilteredElements &&
    beginnerFriendlyFilteredElements.length === NUMBER_OF_RESULTS_PER_COLLECTION
  ) {
    collectionSets.push({
      collectionId: 'beginner-friendly',
      collectionTitle: _t('Beginner Friendly'),
      entries: beginnerFriendlyFilteredElements,
    });
    filters.push({ id: 'beginner-friendly', filterName: _t('Beginner-friendly') });
  }
  return {
    headline: skillTaxonomyData?.skillName || '',
    description: truncateSkillDescription(skillTaxonomyData?.skillDescription || ''),
    isCollectionsLoading: isLoading,
    collectionSets,
    filters,
    layout: MODULE_LAYOUTS.text2image10,
  };
};

export const getDifficultySearchCollections = (difficulty?: SearchProductDifficultyLevel | null) => {
  switch (difficulty) {
    case 'BEGINNER':
      return PRODUCT_DIFFICULTY_LEVELS.Beginner;
    case 'INTERMEDIATE':
      return PRODUCT_DIFFICULTY_LEVELS.Intermediate;
    case 'ADVANCED':
      return PRODUCT_DIFFICULTY_LEVELS.Advanced;
    case 'MIXED':
      return PRODUCT_DIFFICULTY_LEVELS.Mixed;
    default:
      return PRODUCT_DIFFICULTY_LEVELS.Beginner;
  }
};

export const mapSearchHitsToCollections = (collectionSets: CollectionSets): CarouselCollection[] => {
  const searchCollections = collectionSets.map((collection): CarouselCollection => {
    const entities = collection.entries?.map(
      (hit, hitIndex): CollectionEntity => ({
        id: hit.id || '',
        imageUrl: hit.imageUrl || '',
        name: hit.translatedName || hit.name || '',
        slug: getHitSlug(hit.url) || '',
        link: hit.url || '',
        partners: hit.partners?.map((partner, partnerIndex) => ({
          id: partnerIndex.toString(),
          logo: hit.partnerLogos?.[partnerIndex] || '',
          name: partner,
        })) || [{ id: '', logo: '', name: '' }],
        productType: LEARNING_PRODUCTS_ENUM_MAP[hit.productType || ''],
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        productDuration: getTranslatedDurations(PRODUCT_DURATIONS_STRING[hit.productDuration || '']),
        partnerLogos: hit.partnerLogos || [],
        skills: hit.translatedSkills || hit.skills || undefined,
        forceOpenInNewTab: true,
        trackingData: {
          entityId: hit.id || '',
          entityIndex: hitIndex,
          name: hit.translatedName || hit.name || '',
          slug: hit.url || '',
          offeringType: LEARNING_PRODUCTS_ENUM_MAP[hit.productType || ''],
          id: hit.id || '',
        },
        productDifficultyLevel: getDifficultySearchCollections(hit.productDifficultyLevel),
        isPathwayContent: getIsPathwayContent(hit),
        avgProductRating: hit.avgProductRating || undefined,
        numProductRatings: hit.numProductRatings || undefined,
      })
    );
    return {
      collectionId: collection.collectionId,
      title: collection.collectionTitle,
      entities,
      collectionTrackingData: {
        label: collection.collectionTitle,
        collectionId: collection.collectionId,
        collectionIndex: 0,
        collectionName: collection.collectionTitle,
        offeringSource: 'openSearch',
        sectionIndex: 0,
        listIndex: 0,
      },
    };
  });
  return searchCollections;
};
